/**
 * Active-work time chip + breakdown popover for the floating
 * top-nav bar.
 *
 * Two exported components:
 *
 *   <DeckTimeChip totalActiveMs taskStartedAt onClick />
 *     Small inline chip. Renders as `MM:SS` (or `H:MM:SS` past
 *     an hour). Live-ticks while `taskStartedAt` is non-null,
 *     so the user sees the counter increment during an
 *     in-flight task. Hidden until the deck has accumulated any
 *     time to avoid a stale "0:00" on a brand-new deck.
 *
 *   <DeckStatsPopover anchorRect onClose ... />
 *     Click target for the chip — anchored to the chip's
 *     bounding rect, click-outside dismisses. Shows the total
 *     plus a per-version breakdown estimated from artifact
 *     timestamp spans. Long-form "exact per-version active
 *     work" needs the task-status endpoint to carry a
 *     version_label (separate follow-up); for now the popover
 *     surfaces the data we have honestly labeled as an
 *     approximation.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Timer } from '@phosphor-icons/react';

// Minimal version reference for the popover. PP main imports
// VersionGroup which carries artifact metadata; PP-CF only needs the
// label here (versions_active_ms is keyed on the label). Caller
// derives this array from signals.artifacts[] (unique version_label
// strings) at the call-site.
interface VersionGroup {
  label: string;
}

const TASK_TTL_MS = 10 * 60 * 1000;

export function formatHMS(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '0:00';
  const totalSec = Math.floor(ms / 1000);
  const hh = Math.floor(totalSec / 3600);
  const mm = Math.floor((totalSec % 3600) / 60);
  const ss = totalSec % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return hh > 0 ? `${hh}:${pad(mm)}:${pad(ss)}` : `${mm}:${pad(ss)}`;
}

// (the previous span-based per-version estimate has been removed —
//  it conflated wall-clock spans with active-work time and could
//  show hours for a deck the agent only worked on for minutes.
//  Per-version time now comes from `deck.versions_active_ms`,
//  accumulated server-side on each task release with the
//  agent-supplied `version_label`. See decks.mjs task-status
//  handler.)

interface DeckTimeChipProps {
  totalActiveMs: number;
  taskStartedAt: number | null;
  onClick?: () => void;
}

export const DeckTimeChip = React.forwardRef<HTMLButtonElement, DeckTimeChipProps>(
  function DeckTimeChip({ totalActiveMs, taskStartedAt, onClick }, ref) {
    const [nowMs, setNowMs] = useState(() => Date.now());
    useEffect(() => {
      if (!taskStartedAt) return;
      setNowMs(Date.now());
      const iv = setInterval(() => setNowMs(Date.now()), 1000);
      return () => clearInterval(iv);
    }, [taskStartedAt]);

    const liveDelta = taskStartedAt
      ? Math.min(Math.max(0, nowMs - taskStartedAt), TASK_TTL_MS)
      : 0;
    const totalMs = (totalActiveMs || 0) + liveDelta;
    if (totalMs <= 0) return null;
    const isLive = !!taskStartedAt;

    return (
      <button
        ref={ref}
        onClick={onClick}
        title="Active-work time — click for breakdown"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '3px 8px',
          fontSize: 'var(--fs-xs)', fontWeight: 500,
          color: 'var(--text-secondary)',
          background: 'transparent', border: 'none',
          borderRadius: 4, cursor: 'pointer',
          fontFamily: 'inherit',
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '0.02em',
          transition: 'background 0.2s, color 0.2s',
        }}
        onMouseOver={e => {
          e.currentTarget.style.background = 'var(--ds-surface-subtle)';
          e.currentTarget.style.color = 'var(--text-primary)';
        }}
        onMouseOut={e => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = 'var(--text-secondary)';
        }}
      >
        {isLive ? (
          <span style={{
            width: 5, height: 5, borderRadius: '50%',
            background: 'var(--brand, #0033FF)',
            animation: 'pp-time-pulse 1.6s ease-in-out infinite',
          }} />
        ) : (
          <Timer size={11} weight="light" style={{ color: 'currentColor' }} />
        )}
        {formatHMS(totalMs)}
        <style>{`@keyframes pp-time-pulse { 0%,100% { opacity: 0.4; } 50% { opacity: 1; } }`}</style>
      </button>
    );
  },
);

interface DeckStatsPopoverProps {
  anchorRect: DOMRect | null;
  onClose: () => void;
  deckTitle: string;
  totalActiveMs: number;
  taskStartedAt: number | null;
  versions: VersionGroup[];
  /** Server-accumulated per-version time. Keys are `version_label`
   *  strings ("v1", "v2", …), values are ms. Versions present in
   *  `versions[]` but absent from this map render "—" (i.e. the
   *  agent didn't pass a `version_label` when acquiring tasks for
   *  that version, typically because the deck pre-dates the
   *  feature). */
  versionsActiveMs?: Record<string, number>;
  /** The version the in-flight task is targeting (server-side
   *  `decks.task_version`). When set + matching a popover row,
   *  that row shows a live tick on top of the persisted total. */
  taskVersion?: string | null;
}

export function DeckStatsPopover({
  anchorRect, onClose, deckTitle, totalActiveMs, taskStartedAt, versions,
  versionsActiveMs = {}, taskVersion = null,
}: DeckStatsPopoverProps) {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!taskStartedAt) return;
    const iv = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [taskStartedAt]);
  const liveDelta = taskStartedAt
    ? Math.min(Math.max(0, nowMs - taskStartedAt), TASK_TTL_MS)
    : 0;
  const totalMs = (totalActiveMs || 0) + liveDelta;

  // Anchor under the chip; clamp to viewport.
  const top = anchorRect ? anchorRect.bottom + 6 : 60;
  const right = anchorRect
    ? Math.max(8, window.innerWidth - anchorRect.right)
    : 24;

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest?.('[data-pp-stats-popover]')) return;
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onClose]);

  return (
    <div
      data-pp-stats-popover
      style={{
        position: 'fixed', top, right, zIndex: 100,
        minWidth: 260, maxWidth: 360,
        background: 'var(--bg-primary)',
        border: '1px solid var(--border)', borderRadius: 8,
        boxShadow: '0 6px 20px var(--ds-shadow-color)',
        padding: '12px 14px',
        fontFamily: 'inherit',
      }}
    >
      <div style={{
        fontSize: 'var(--fs-2xs)', fontWeight: 500, color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.08em',
        marginBottom: 6,
      }}>
        Active-work time
      </div>
      <div style={{
        fontSize: 'var(--fs-xs)', color: 'var(--text-muted)',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        marginBottom: 10,
      }}>
        {deckTitle || 'Untitled deck'}
      </div>

      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        paddingBottom: 8, marginBottom: 10,
        borderBottom: '1px solid var(--border)',
      }}>
        <span style={{
          fontSize: 'var(--fs-xs)', color: 'var(--text-muted)',
          textTransform: 'uppercase', letterSpacing: '0.04em',
        }}>Total</span>
        <span style={{
          fontSize: 'var(--fs-md)', fontWeight: 500,
          color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums',
        }}>
          {formatHMS(totalMs)}
        </span>
      </div>

      {versions.length > 0 ? (
        <>
          <div style={{
            fontSize: 'var(--fs-2xs)', fontWeight: 500, color: 'var(--text-muted)',
            textTransform: 'uppercase', letterSpacing: '0.08em',
            marginBottom: 6,
          }}>
            By version ({versions.length})
          </div>
          {versions.map(v => {
            const persisted = Number(versionsActiveMs[v.label]) || 0;
            const isLiveForVersion = taskVersion === v.label;
            const versionMs = persisted + (isLiveForVersion ? liveDelta : 0);
            return (
              <div key={v.label} style={{
                display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                padding: '4px 0',
                fontSize: 'var(--fs-sm)', color: 'var(--text-primary)',
                fontVariantNumeric: 'tabular-nums',
              }}>
                <span style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {v.label}
                  {isLiveForVersion && (
                    <span style={{
                      display: 'inline-block',
                      width: 5, height: 5, borderRadius: '50%',
                      background: 'var(--brand, #0033FF)',
                      marginLeft: 6,
                      animation: 'pp-time-pulse 1.6s ease-in-out infinite',
                    }} />
                  )}
                </span>
                <span style={{ color: versionMs > 0 ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                  {versionMs > 0 ? formatHMS(versionMs) : '—'}
                  <span style={{
                    fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', marginLeft: 6,
                  }}>
                    {v.artifacts.length} artifact{v.artifacts.length === 1 ? '' : 's'}
                  </span>
                </span>
              </div>
            );
          })}
          <div style={{
            marginTop: 8, paddingTop: 8,
            borderTop: '1px solid var(--border)',
            fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)',
            lineHeight: 1.4,
          }}>
            Time accumulates when the agent runs a task tagged
            with that version. Older decks may show "—" if their
            tasks pre-date version tagging.
          </div>
        </>
      ) : (
        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
          No versions yet.
        </div>
      )}
    </div>
  );
}
